import { Row, Col, Card, Progress, List, Button, Typography, Space, Tag, Select, Table, Modal, Input, Upload, message, Breadcrumb, Popconfirm, Empty, Spin, Form, Divider, Radio, Checkbox } from 'antd';
import {
  ProjectOutlined, FileTextOutlined, ApiOutlined, CloudServerOutlined,
  PlusOutlined, DesktopOutlined, UploadOutlined, FolderAddOutlined,
  DeleteOutlined, EditOutlined, ReloadOutlined, FileOutlined,
  FolderOutlined, FolderOpenOutlined, HomeOutlined, InboxOutlined, SaveOutlined,
  CheckCircleOutlined, CloseCircleOutlined, TeamOutlined,
  ThunderboltOutlined, DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useProjectStore } from '../../stores/projectStore';
import { projectApi } from '../../services/projectService';
import { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import './Dashboard.css';

const { Title, Text } = Typography;
const { Dragger } = Upload;
const { TextArea } = Input;

interface Workbench { id: string; name: string; type: 'local' | 'ssh'; workPath: string; status: string; projectId?: string; }
interface FileItem { name: string; path: string; relativePath: string; type: 'file' | 'directory'; size?: number; mtime?: string; children?: FileItem[]; }

const featurePermissions = [
  { key: '/digital-office', label: '数字员工', desc: '多Agent协作' },
  { key: '/ai-demand-center', label: 'AI需求共创', desc: '产前需求协同' },
  { key: '/ppt-designer', label: 'AI PPT设计', desc: '可编辑汇报材料' },
  { key: '/design-creator', label: '设计图创作', desc: '生图与上传改字' },
  { key: '/skill-market', label: '交通智件', desc: '能力市场' },
  { key: '/tech-components', label: '技术组件', desc: '组件资产' },
];

function formatSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatMtime(iso?: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

const TokenCell: React.FC<{ userId: string }> = ({ userId }) => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get('/users/tokens/all').then(r => {
      const found = (r.data || []).find((u: any) => u.userId === userId);
      setStats(found || null);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [userId]);
  if (loading) return <Spin size="small" />;
  if (!stats || !stats.totalTokens) return <Text type="secondary">暂无</Text>;
  return <span style={{ fontSize: 12 }}><ThunderboltOutlined style={{ color: '#fa8c16' }} /> {formatTokens(stats.totalTokens)} <Text type="secondary" style={{ fontSize: 10 }}>({stats.requestCount}次)</Text></span>;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { projects, setProjects } = useProjectStore();
  const [wbCount, setWbCount] = useState(0);

  // 文件管理 state
  const [workbenches, setWorkbenches] = useState<Workbench[]>([]);
  const [selectedWbId, setSelectedWbId] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [currentPath, setCurrentPath] = useState<string>('');
  const [wbManageOpen, setWbManageOpen] = useState(false);
  const [editingWb, setEditingWb] = useState<Workbench|null>(null);
  const [wbForm] = Form.useForm();
  const [wbType, setWbType] = useState<'local'|'ssh'>('local');
  const [wbSaving, setWbSaving] = useState(false);
  const [wbTesting, setWbTesting] = useState<string|null>(null);
  // 用户管理
  const isAdmin = useAuthStore(s => s.user?.roles?.includes('admin') ?? false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserForm] = Form.useForm();
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [userList, setUserList] = useState<any[]>([]);
  const [userListLoading, setUserListLoading] = useState(false);
  const [miniappApplications, setMiniappApplications] = useState<any[]>([]);
  const [miniappLoading, setMiniappLoading] = useState(false);
  const [approvingMiniappId, setApprovingMiniappId] = useState('');
  const [rejectingMiniappId, setRejectingMiniappId] = useState('');
  const [permModalOpen, setPermModalOpen] = useState(false);
  const [permUserId, setPermUserId] = useState('');
  const [permFeatures, setPermFeatures] = useState<string[]>([]);
  const [permSaving, setPermSaving] = useState(false);
  const [featureInfoOpen, setFeatureInfoOpen] = useState(false);
  const loadUsers = async () => { setUserListLoading(true); try { const r = await api.get('/users'); const data = r.data?.items || r.data?.data || r.data || []; setUserList(data.map((u: any) => ({ ...u, roles: Array.isArray(u.roles) ? u.roles : (u.userRoles || []).map((ur: any) => ur.role?.code || ur.role) }))); } catch {} setUserListLoading(false); };
  const loadMiniappApplications = async () => { setMiniappLoading(true); try { const r = await api.get('/users/miniapp/applications'); setMiniappApplications(r.data || []); } catch (e:any) { message.error(e?.response?.data?.message || '加载小程序用户失败'); } setMiniappLoading(false); };
  const approveMiniappApplication = async (id: string) => {
    setApprovingMiniappId(id);
    try {
      await api.put(`/users/miniapp/applications/${id}/approve`);
      message.success('已通过小程序用户申请');
      loadMiniappApplications();
      loadUsers();
    } catch (e: any) {
      message.error(e?.response?.data?.message || '审批失败');
    }
    setApprovingMiniappId('');
  };
  const rejectMiniappApplication = async (id: string) => {
    setRejectingMiniappId(id);
    try {
      await api.put(`/users/miniapp/applications/${id}/reject`, { reason: '' });
      message.success('已驳回小程序用户申请');
      loadMiniappApplications();
    } catch (e: any) {
      message.error(e?.response?.data?.message || '驳回失败');
    }
    setRejectingMiniappId('');
  };
  const [files, setFiles] = useState<FileItem[]>([]);
  const [fileLoading, setFileLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorPath, setEditorPath] = useState('');
  const [editorContent, setEditorContent] = useState('');
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FileItem | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<{ name: string; path: string }[]>([]);

  // 工作台创建弹窗
  const [wbModalOpen, setWbModalOpen] = useState(false);
  const handleCreateWb = async (values: any) => {
    setWbSaving(true);
    try {
      await api.post('/dev-workspace/workbenches', { name: values.name, type: 'local', workPath: values.workPath });
      message.success('工作台创建成功');
      setWbModalOpen(false); wbForm.resetFields(); loadWorkbenches();
    } catch (e: any) { message.error(e.response?.data?.message || '创建失败'); }
    setWbSaving(false);
  };

  useEffect(() => {
    loadProjects();
    loadWorkbenches();
  }, []);

  useEffect(() => {
    if (isAdmin) { loadUsers(); loadMiniappApplications(); }
  }, [isAdmin]);

  const loadProjects = async () => {
    try { const data = await projectApi.getAll(); setProjects(data); } catch (e) { console.error(e); }
  };

  const loadWorkbenches = async () => {
    try {
      const r = await api.get('/dev-workspace/workbenches');
      // 只保留未删除的工作台
      const wbs = (r.data || []).filter((w: Workbench) => !w.deletedAt);
      setWorkbenches(wbs);
      setWbCount(wbs.length);
      // 如果有选中的项目，优先选该项目的工作台
      if (wbs.length > 0 && !selectedWbId) {
        const pid = selectedProjectId || projects[0]?.id;
        const projectWbs = pid ? wbs.filter((w: Workbench) => w.projectId === pid) : wbs;
        const first = projectWbs.length > 0 ? projectWbs[0] : wbs[0];
        setSelectedWbId(first.id);
        if (pid && !selectedProjectId) setSelectedProjectId(pid);
        loadFiles(first.id, '');
      }
    } catch {}
  };

  const loadFiles = async (wbId?: string, subPath?: string) => {
    const id = wbId || selectedWbId;
    if (!id) return;
    setFileLoading(true);
    try {
      const r = await api.get('/dev-workspace/files/tree', { params: { workbenchId: id, path: subPath || currentPath } });
      setFiles(r.data || []);
      // 构建面包屑
      const wb = workbenches.find(w => w.id === id);
      const crumbs: { name: string; path: string }[] = [{ name: wb?.name || '工作区', path: '' }];
      if (subPath !== undefined) setCurrentPath(subPath || '');
      const cp = subPath !== undefined ? (subPath || '') : currentPath;
      if (cp) {
        const parts = cp.split('/').filter(Boolean);
        let acc = '';
        parts.forEach(p => { acc = acc ? `${acc}/${p}` : p; crumbs.push({ name: p, path: acc }); });
      }
      setBreadcrumbs(crumbs);
    } catch (e: any) {
      message.error(e.response?.data?.message || '加载文件失败');
    }
    setFileLoading(false);
  };

  const handleBreadcrumbClick = (path: string) => {
    setCurrentPath(path);
    loadFiles(undefined, path);
  };

  const handleFileClick = (item: FileItem) => {
    if (item.type === 'directory') {
      setCurrentPath(item.relativePath);
      loadFiles(undefined, item.relativePath);
    } else {
      openEditor(item.path);
    }
  };

  const openEditor = async (filePath: string) => {
    setEditorPath(filePath);
    setEditorOpen(true);
    setEditorLoading(true);
    setEditorContent('');
    try {
      const r = await api.get('/dev-workspace/files/read', { params: { workbenchId: selectedWbId, path: filePath } });
      setEditorContent(r.data?.content || '');
    } catch (e: any) {
      message.error(e.response?.data?.message || '读取失败');
    }
    setEditorLoading(false);
  };

  const saveFile = async () => {
    setEditorSaving(true);
    try {
      await api.put('/dev-workspace/files/write', { workbenchId: selectedWbId, path: editorPath, content: editorContent });
      message.success('保存成功');
      setEditorOpen(false);
      loadFiles();
    } catch (e: any) {
      message.error(e.response?.data?.message || '保存失败');
    }
    setEditorSaving(false);
  };

  const handleDownload = async (item: FileItem) => {
    try {
      const token = useAuthStore.getState().token;
      const params = new URLSearchParams({ workbenchId: selectedWbId, path: item.path });
      const response = await fetch(`/api/dev-workspace/files/download?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const text = await response.text();
        let msg = '下载失败';
        try { const json = JSON.parse(text); msg = json.message || msg; } catch {}
        message.error(msg);
        return;
      }
      const blob = await response.blob();
      // 优先从 Content-Disposition 取文件名，fallback 到原始文件名
      const disposition = response.headers.get('Content-Disposition');
      let fileName = item.name;
      if (disposition) {
        const match = disposition.match(/filename\*=UTF-8''([^;]+)/);
        if (match) {
          fileName = decodeURIComponent(match[1]);
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      message.error('下载失败: ' + (e.message || '网络错误'));
    }
  };

  const handleUpload = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    form.append('workbenchId', selectedWbId);
    if (currentPath) form.append('targetPath', currentPath);

    try {
      await api.post('/dev-workspace/files/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      message.success(`${file.name} 上传成功`);
      loadFiles();
      setUploadOpen(false);
    } catch (e: any) {
      message.error(e.response?.data?.message || '上传失败');
    }
    return false; // prevent default upload behavior
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete('/dev-workspace/files/delete', {
        params: { workbenchId: selectedWbId, path: deleteTarget.path },
      });
      message.success(`已删除: ${deleteTarget.name}`);
      setDeleteTarget(null);
      loadFiles();
    } catch (e: any) {
      message.error(e.response?.data?.message || '删除失败');
    }
  };

  const handleNewFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await api.post('/dev-workspace/files/folder', {
        workbenchId: selectedWbId,
        path: currentPath,
        folderName: newFolderName.trim(),
      });
      message.success('文件夹创建成功');
      setNewFolderOpen(false);
      setNewFolderName('');
      loadFiles();
    } catch (e: any) {
      message.error(e.response?.data?.message || '创建失败');
    }
  };

  const selectedWb = workbenches.find(w => w.id === selectedWbId);

  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      render: (_: string, item: FileItem) => (
        <a onClick={() => handleFileClick(item)} style={{ cursor: 'pointer' }}>
          <Space size={8}>
            {item.type === 'directory' ? <FolderOutlined style={{ color: '#faad14', fontSize: 16 }} /> : <FileOutlined style={{ color: '#999', fontSize: 16 }} />}
            {item.name}
          </Space>
        </a>
      ),
    },
    { title: '大小', dataIndex: 'size', key: 'size', width: 100, render: (_: any, item: FileItem) => item.type === 'file' ? formatSize(item.size) : '-' },
    { title: '修改时间', dataIndex: 'mtime', key: 'mtime', width: 140, render: (v: string) => formatMtime(v) },
    {
      title: '操作',
      key: 'actions',
      width: 140,
      render: (_: any, item: FileItem) => (
        <Space size={4}>
          {item.type === 'file' && <Button size="small" type="text" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); openEditor(item.path); }} />}
          {item.type === 'file' && <Button size="small" type="text" icon={<DownloadOutlined />} onClick={(e) => { e.stopPropagation(); handleDownload(item); }} />}
          <Popconfirm title={`确认删除 ${item.name}？` + (item.type === 'directory' ? ' 将删除目录下所有内容！' : '')} onConfirm={() => { setDeleteTarget(item); setTimeout(handleDelete, 0); }} onCancel={() => setDeleteTarget(null)}>
            <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const recentProjects = projects.slice(0, 5);

  return (
    <div className="dashboard" style={{ width: '100%' }}>
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <Card className='lg-hero-card' style={{ color: '#fff' }}>
          <Row gutter={[20, 20]} align="middle">
            <Col xs={24} lg={14}>
              <Title level={3} style={{ color: '#fff', marginTop: 0, marginBottom: 12 }}>业务工作台</Title>
              <div style={{ fontSize: 15, lineHeight: 1.8, opacity: 0.96 }}>
                在这里查看项目推进、需求产出、接口准备和部署状态。页面已重新整理卡片间距和层次，方便你一眼看到重点。
              </div>
            </Col>
            <Col xs={24} lg={10}>
              <Space direction="vertical" size={10} style={{ width: '100%' }}>
                <Card size="small" style={{ borderRadius: 16, background: 'rgba(255,255,255,0.12)', borderColor: 'rgba(255,255,255,0.18)' }}>
                  <Space direction="vertical" size={10} style={{ width: '100%' }}>
                    <Tag color="blue">今日重点</Tag>
                    <div style={{ color: '#fff' }}>优先完成需求确认与原型评审，再进入前后端交付准备。</div>
                  </Space>
                </Card>
                {isAdmin && (
                  <Button type="primary" onClick={() => setFeatureInfoOpen(true)} style={{ alignSelf: 'flex-start' }}>
                    功能权限管理
                  </Button>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        <Row gutter={[20, 20]} className="stats-row">
          {[
            { title: '项目总数', value: projects.length, icon: <ProjectOutlined />, color: '#1677ff' },
            { title: '进行中', value: projects.filter((p) => p.status !== 'deployed').length, icon: <FileTextOutlined />, color: '#fa8c16' },
            { title: 'API 数量', value: 0, icon: <ApiOutlined />, color: '#52c41a' },
            { title: '已部署', value: projects.filter((p) => p.status === 'deployed').length, icon: <CloudServerOutlined />, color: '#722ed1' },
          ].map((s, i) => (
            <Col xs={24} sm={12} md={6} key={i}>
              <Card className='lg-stat-card' bodyStyle={{ padding: 20 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: 13, fontWeight: 600 }}>{s.title}</Text>
                    <div style={{ fontSize: 32, fontWeight: 900, color: '#1e293b', lineHeight: 1.2, marginTop: 4 }}>{s.value}</div>
                  </div>
                  <div style={{
                    width: 42, height: 42, borderRadius: 12,
                    background: `linear-gradient(135deg, ${s.color}15, ${s.color}08)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 20, color: s.color,
                  }}>
                    {s.icon}
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* ═══════════ 快捷入口导航 ═══════════ */}
        <div style={{ marginBottom: 0 }}>
          <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 12, color: '#1e293b' }}>
            🚀 快捷入口
          </Text>
          <Row gutter={[16, 16]}>
            {featurePermissions.map((fp) => (
              <Col xs={12} sm={8} md={6} lg={4} key={fp.key}>
                <Card
                  className="lg-glass-card"
                  hoverable
                  bodyStyle={{ padding: '20px 16px', textAlign: 'center', cursor: 'pointer' }}
                  onClick={() => navigate(fp.key)}
                >
                  <div style={{ fontSize: 28, marginBottom: 8 }}>
                    {fp.key === '/digital-office' ? '🤖' :
                     fp.key === '/ai-demand-center' ? '💡' :
                     fp.key === '/ppt-designer' ? '📊' :
                     fp.key === '/design-creator' ? '🎨' :
                     fp.key === '/skill-market' ? '🧩' :
                     '🔧'}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{fp.label}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{fp.desc}</div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        <Row gutter={[20, 20]} style={{ marginBottom: 0 }}>
          <Col xs={24} lg={24}>
            <Card title="最近项目" className="lg-glass-card"
              extra={wbCount === 0
                ? <Button type="primary" icon={<DesktopOutlined />} onClick={() => setWbModalOpen(true)} className="lg-btn-primary">请先创建工作台</Button>
                : <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/projects')} className="lg-btn-primary">新建项目</Button>
              }
            >
              <List
                dataSource={recentProjects}
                renderItem={(project: any) => {
                  const isOwner = project.ownerId === useAuthStore.getState().user?.id;
                  const shares = project.shares || [];
                  return (
                  <List.Item style={{ paddingTop: 18, paddingBottom: 18 }}
                    actions={[<Button type="link" onClick={() => navigate(`/projects/${project.id}`)}>查看</Button>]}
                  >
                    <List.Item.Meta
                      title={<Space size={8}>{project.name}
                        {!isOwner && project.owner && <Tag color="orange" style={{fontSize:11}}>来自 {project.owner.username || project.owner.name}</Tag>}
                        {isOwner && shares.length > 0 && <Tag color="green" style={{fontSize:11}}>已共享给 {shares.map((s:any)=>s.sharedWithUser?.username||s.sharedWithUser?.name).join(', ')}</Tag>}
                      </Space>}
                      description={project.description || '暂无描述'}
                    />
                    <Progress percent={Math.round((['created', 'knowledge', 'requirement', 'prototype', 'api_design', 'architecture', 'development', 'testing', 'deployed'].indexOf(project.status) + 1) / 9 * 100)} size="small" style={{ width: 200 }} />
                  </List.Item>
                  );
                }}
                locale={{ emptyText: '暂无项目，点击上方按钮创建' }}
              />
            </Card>
      </Col>
        </Row>

        {/* ═══════════ 用户管理（仅admin） ═══════════ */}
        {isAdmin && (
          <>
          <Card title={<Space><TeamOutlined /> Web 用户管理</Space>} size="small" style={{marginBottom:16}} extra={<Space>
            <Button size="small" icon={<ReloadOutlined />} onClick={loadUsers}>刷新</Button>
            <Button size="small" type="primary" icon={<PlusOutlined/>} onClick={()=>{setAddUserOpen(true);addUserForm.resetFields();}}>添加用户</Button>
          </Space>}>
            <Table size="small" dataSource={userList} rowKey="id" loading={userListLoading} pagination={false}
              columns={[
                {title:'用户名',dataIndex:'username',width:100},
                {title:'昵称',dataIndex:'name',width:100},
                {title:'角色',dataIndex:'roles',width:100,render:(v:string[])=>v?.map((r:string)=><Tag key={r} color={r==='admin'?'red':'blue'}>{r}</Tag>)},
                {title:'状态',dataIndex:'status',width:70,render:(v:string)=><Tag color={v==='active'?'green':'default'}>{v==='active'?'正常':v}</Tag>},
                {title:'Token用量(30天)',width:160,render:(_:any,r:any)=><TokenCell userId={r.id} />},
                {title:'操作',width:160,render:(_:any,r:any)=>(
                  <Space size={4}>
                    <Button size="small" type="link" danger={r.status==='active'} onClick={async ()=>{
                      const newStatus = r.status==='active'?'disabled':'active';
                      try {
                        await api.put(`/users/${r.id}/status`,{status:newStatus});
                        message.success(r.status==='active'?`已禁用 ${r.username}`:`已启用 ${r.username}`);
                        loadUsers();
                      } catch (e: any) {
                        message.error(e?.response?.data?.message || '操作失败');
                      }
                    }}>{r.status==='active'?'禁用':'启用'}</Button>
                    <Button size="small" type="link" onClick={()=>{setPermUserId(r.id);setPermFeatures(r.allowedFeatures||[]);setPermModalOpen(true);}}>权限</Button>
                  </Space>
                )},
              ]}
            />
          </Card>

          <Card title={<Space><TeamOutlined /> 小程序用户管理</Space>} size="small" style={{marginBottom:16}} extra={<Space>
            <Button size="small" icon={<ReloadOutlined />} onClick={loadMiniappApplications}>刷新</Button>
          </Space>}>
            <Table size="small" dataSource={miniappApplications} rowKey="id" loading={miniappLoading} pagination={{pageSize:8}}
              columns={[
                {title:'昵称',dataIndex:'nickname',width:120,render:(v:string)=><span>{v || '微信用户'}</span>},
                {title:'手机号',dataIndex:'phone',width:120,render:(v:string)=><Text copyable={!!v}>{v || '-'}</Text>},
                {title:'openid',dataIndex:'openid',width:180,ellipsis:true,render:(v:string)=><Text copyable style={{maxWidth:170}} ellipsis>{v}</Text>},
                {title:'申请时间',dataIndex:'appliedAt',width:160,render:(v:string)=>v?new Date(v).toLocaleString('zh-CN'):'-'},
                {title:'状态',dataIndex:'status',width:90,render:(v:string)=><Tag color={v==='approved'?'green':v==='rejected'?'red':'orange'}>{v==='approved'?'已通过':v==='rejected'?'已拒绝':'待审批'}</Tag>},
                {title:'绑定用户',width:140,render:(_:any,r:any)=>r.user ? <Text>{r.user.name || r.user.username}</Text> : r.userId ? <Text copyable>{r.userId}</Text> : <Text type="secondary">未绑定</Text>},
                {title:'操作',width:200,render:(_:any,r:any)=>r.status==='pending' ? (
                  <Space size={4}>
                    <Popconfirm title="确认通过该小程序用户申请？" onConfirm={()=>approveMiniappApplication(r.id)} okText="通过" cancelText="取消">
                      <Button size="small" type="primary" loading={approvingMiniappId===r.id}>通过</Button>
                    </Popconfirm>
                    <Popconfirm title="确认驳回该小程序用户申请？" onConfirm={()=>rejectMiniappApplication(r.id)} okText="驳回" cancelText="取消">
                      <Button size="small" danger loading={rejectingMiniappId===r.id}>驳回</Button>
                    </Popconfirm>
                  </Space>
                ) : <Text type="secondary">-</Text>},
              ]}
              locale={{ emptyText: <Empty description="暂无小程序用户申请" /> }}
            />
          </Card>
          </>
        )}

        {/* ═══════════ 文件管理器 ═══════════ */}
        <Card className="lg-glass-card"
          title={
            <Space>
              <FolderOpenOutlined style={{ color: '#faad14' }} />
              <span>文件管理</span>
            </Space>
          }
          extra={
            <Space>
              {projects.length > 0 && (
                <Select size="small" value={selectedProjectId || undefined} style={{width:160}} placeholder="选择项目"
                  onChange={(v) => {
                    setSelectedProjectId(v);
                    setCurrentPath('');
                    const projectWbs = workbenches.filter(w => w.projectId === v);
                    if (projectWbs.length > 0) {
                      setSelectedWbId(projectWbs[0].id);
                      loadFiles(projectWbs[0].id, '');
                    } else if (workbenches.length > 0) {
                      setSelectedWbId(workbenches[0].id);
                      loadFiles(workbenches[0].id, '');
                    }
                  }}
                  options={projects.map(p => ({value: p.id, label: p.name}))}
                />
              )}
              {workbenches.length === 0 ? (
                <Text type="secondary">无可用工作台</Text>
              ) : (
                <>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    <DesktopOutlined /> {selectedWb?.workPath || workbenches[0]?.workPath}
                  </Text>
                  <Button size="small" icon={<EditOutlined />} onClick={() => setWbManageOpen(true)}>管理工作台</Button>
                  <Button size="small" icon={<ReloadOutlined />} onClick={() => loadFiles()}>刷新</Button>
                </>
              )}
            </Space>
          }
        >
          {!selectedWbId ? (
            <Empty description="请先选择工作台" />
          ) : (
            <>
              {/* 工具栏 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Breadcrumb items={[
                  { title: <a onClick={() => handleBreadcrumbClick('')}><HomeOutlined /> 根目录</a> },
                  ...breadcrumbs.slice(1).map(b => ({ title: <a onClick={() => handleBreadcrumbClick(b.path)}>{b.name}</a> })),
                ]} />
                <Space>
                  <Button size="small" icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>上传文件</Button>
                  <Button size="small" icon={<FolderAddOutlined />} onClick={() => setNewFolderOpen(true)}>新建文件夹</Button>
                </Space>
              </div>
              {selectedWb && <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>路径: {selectedWb.workPath}{currentPath ? `/${currentPath}` : ''}</Text>}

              {/* 文件列表 */}
              <Table
                dataSource={files}
                columns={columns}
                rowKey="path"
                size="small"
                loading={fileLoading}
                pagination={false}
                locale={{ emptyText: <Empty description="此目录为空" /> }}
                onRow={(item) => ({
                  onDoubleClick: () => handleFileClick(item),
                  style: { cursor: 'pointer' },
                })}
              />
            </>
          )}
        </Card>
      </Space>

      {/* ── 上传弹窗 ── */}
      <Modal title="上传文件" open={uploadOpen} onCancel={() => setUploadOpen(false)} footer={null} width={520}>
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">上传到: {selectedWb?.workPath}{currentPath ? `/${currentPath}` : ''}</Text>
        </div>
        <Dragger
          multiple
          beforeUpload={handleUpload}
          showUploadList={false}
          accept="*/*"
        >
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
          <p className="ant-upload-hint">支持任意类型文件</p>
        </Dragger>
      </Modal>

      {/* ── 新建文件夹弹窗 ── */}
      <Modal title="新建文件夹" open={newFolderOpen} onCancel={() => { setNewFolderOpen(false); setNewFolderName(''); }}
        onOk={handleNewFolder} confirmLoading={false}
      >
        <Input placeholder="输入文件夹名称" value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
          onPressEnter={handleNewFolder} autoFocus
        />
      </Modal>

      {/* ── 文件编辑弹窗 ── */}
      <Modal
        title={<Space><EditOutlined />{editorPath.split('/').pop()}</Space>}
        open={editorOpen}
        onCancel={() => setEditorOpen(false)}
        width={900}
        footer={[
          <Button key="cancel" onClick={() => setEditorOpen(false)}>取消</Button>,
          <Button key="save" type="primary" icon={<SaveOutlined />} loading={editorSaving} onClick={saveFile}>保存</Button>,
        ]}
      >
        <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>{editorPath}</div>
        {editorLoading ? <Spin /> : (
          <TextArea
            value={editorContent}
            onChange={e => setEditorContent(e.target.value)}
            autoSize={{ minRows: 15, maxRows: 30 }}
            style={{ fontFamily: 'monospace', fontSize: 13 }}
          />
        )}
      </Modal>

      {/* ── 工作台创建弹窗 ── */}
      <Modal title="创建工作台" open={wbModalOpen} onCancel={() => setWbModalOpen(false)} footer={null} width={480}>
        <Form form={wbForm} layout="vertical" onFinish={handleCreateWb} initialValues={{workPath:'/etc_data/'}}>
          <Form.Item name="name" label="工作台名称" rules={[{ required: true }]}>
            <Input placeholder="例如：我的工作台" />
          </Form.Item>
          <Form.Item name="workPath" label="工作目录" rules={[{ required: true }]}
            extra={<Text type="secondary" style={{fontSize:11}}>项目将作为子目录创建在此路径下。每个用户只能创建一个工作台。</Text>}>
            <Input placeholder="/etc_data/admin/" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={wbSaving}>创建</Button>
        </Form>
      </Modal>

      {/* ── 管理工作台弹窗 ── */}
      <Modal title="管理工作台" open={wbManageOpen} onCancel={()=>{setWbManageOpen(false);setEditingWb(null);}} footer={null} width={640} destroyOnClose>
        {workbenches.map(wb=>(
          <Card key={wb.id} size="small" style={{marginBottom:8}} extra={
            <Space size={4}>
              <Button size="small" icon={<ReloadOutlined/>} loading={wbTesting===wb.id} onClick={async()=>{setWbTesting(wb.id);try{await api.post(`/dev-workspace/workbenches/${wb.id}/test`);}catch{}setWbTesting(null);loadWorkbenches();}}>测试</Button>
              <Button size="small" icon={<EditOutlined/>} onClick={()=>{setEditingWb(wb);setWbType(wb.type);wbForm.setFieldsValue({name:wb.name,type:wb.type,workPath:wb.workPath,host:wb.host,port:wb.port||22,username:wb.username});}}>编辑</Button>
              <Popconfirm title="确认删除？" onConfirm={async()=>{try{await api.delete(`/dev-workspace/workbenches/${wb.id}`);if(selectedWbId===wb.id)setSelectedWbId('');loadWorkbenches();}catch{}}}><Button size="small" danger icon={<DeleteOutlined/>}/></Popconfirm>
            </Space>
          }>
            <Space size={4}><strong>{wb.name}</strong><Tag color={wb!=='ssh'?'blue':'orange'}>{wb.type==='local'?'平台':'SSH'}</Tag>{wb.status==='active'?<CheckCircleOutlined style={{color:'#52c41a'}}/>:<CloseCircleOutlined style={{color:'#ff4d4f'}}/>}</Space>
            <div style={{fontSize:11,color:'#888',marginTop:4}}>{wb.type==='local'?wb.workPath:`${wb.username}@${wb.host}:${wb.port} → ${wb.workPath}`}</div>
          </Card>
        ))}
        <Divider/>
        <Card size="small" title={editingWb?`编辑: ${editingWb.name}`:'添加工作台'}>
          <Form form={wbForm} layout="vertical" onFinish={async(values:any)=>{setWbSaving(true);try{if(editingWb)await api.put(`/dev-workspace/workbenches/${editingWb.id}`,values);else await api.post('/dev-workspace/workbenches',values);setWbManageOpen(false);setEditingWb(null);wbForm.resetFields();loadWorkbenches();}catch(e:any){message.error(e?.response?.data?.message||e?.message||'保存失败');}setWbSaving(false);}} initialValues={{type:'local',port:22}}>
            <Form.Item name="name" label="名称" rules={[{required:true}]}><Input/></Form.Item>
            <Form.Item name="type" label="类型"><Radio.Group onChange={e=>setWbType(e.target.value)}><Radio.Button value="local"><DesktopOutlined/> 平台服务器</Radio.Button><Radio.Button value="ssh"><CloudServerOutlined/> SSH远程</Radio.Button></Radio.Group></Form.Item>
            {wbType==='ssh'&&<Card size="small" title="SSH" style={{marginBottom:12,background:'#fafafa'}}><div style={{display:'flex',gap:12}}><Form.Item name="host" label="主机" rules={[{required:true}]} style={{flex:2}}><Input/></Form.Item><Form.Item name="port" label="端口" style={{flex:1}}><Input type="number"/></Form.Item></div><Form.Item name="username" label="用户名" rules={[{required:true}]}><Input/></Form.Item><Form.Item name="password" label="密码"><Input.Password placeholder="加密存储"/></Form.Item></Card>}
            <Form.Item name="workPath" label="工作路径" rules={[{required:true}]}><Input placeholder={wbType==='local'?'/etc_data/workspaces/my-project':'/home/dev/projects'}/></Form.Item>
            <Button type="primary" htmlType="submit" block loading={wbSaving}>{editingWb?'保存':'创建'}</Button>
          </Form>
        </Card>
      </Modal>

      {/* ── 添加用户弹窗（仅admin） ── */}
      <Modal className="dashboard-add-user-modal" title={null} open={addUserOpen} onCancel={()=>setAddUserOpen(false)} footer={null} width={620} destroyOnClose>
        <div className="df-form-modal-head">
          <div>
            <span className="df-form-modal-kicker">用户管理</span>
            <h2>添加用户</h2>
            <p>创建可登录账号，后续可在用户列表中分配功能权限和查看 Token 用量。</p>
          </div>
          <span className="df-form-modal-icon"><TeamOutlined /></span>
        </div>
        <Form className="df-form-modal-body" form={addUserForm} layout="vertical" onFinish={async(values:any)=>{
          setAddUserLoading(true);
          try {
            await api.post('/auth/register', { username: values.username, password: values.password, name: values.nickname||values.username });
            message.success(`用户 ${values.username} 创建成功`);
            setAddUserOpen(false);
            addUserForm.resetFields();
            loadUsers();
          } catch(e:any) {
            message.error(e?.response?.data?.message || e?.message || '创建失败');
          }
          setAddUserLoading(false);
        }}>
          <div className="df-form-grid df-form-grid-two">
            <Form.Item name="username" label="用户名" rules={[{required:true,message:'请输入用户名'}]}><Input placeholder="登录账号"/></Form.Item>
            <Form.Item name="nickname" label="昵称"><Input placeholder="显示名称（可选）"/></Form.Item>
          </div>
          <div className="df-form-grid df-form-grid-two">
            <Form.Item name="password" label="密码" rules={[{required:true,message:'请输入密码'},{min:6,message:'至少6位'}]}><Input.Password placeholder="登录密码"/></Form.Item>
            <Form.Item name="confirmPassword" label="确认密码" dependencies={['password']} rules={[{required:true,message:'请确认密码'},({getFieldValue})=>({validator(_,value){if(!value||getFieldValue('password')===value)return Promise.resolve();return Promise.reject(new Error('两次密码不一致'));}})]}><Input.Password placeholder="再次输入密码"/></Form.Item>
          </div>
          <div className="df-form-tip-card">创建后默认拥有基础账号能力，功能模块可在用户列表的“权限”中单独配置。</div>
          <Form.Item className="df-form-modal-actions">
            <Button onClick={()=>setAddUserOpen(false)}>取消</Button>
            <Button type="primary" htmlType="submit" loading={addUserLoading} icon={<PlusOutlined/>}>创建用户</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal className="dashboard-permission-modal" title={null} open={permModalOpen} onCancel={()=>setPermModalOpen(false)} width={680}
        footer={<>
          <Button onClick={()=>setPermModalOpen(false)}>取消</Button>
          <Button type="primary" loading={permSaving} onClick={async ()=>{
            setPermSaving(true);
            try {
              await api.put(`/users/${permUserId}/features`,{features:permFeatures});
              message.success('权限已更新');
              setPermModalOpen(false);
              loadUsers();
            } catch(e:any){ message.error(e?.response?.data?.message||'保存失败'); }
            setPermSaving(false);
          }}>保存</Button>
        </>}
      >
        <div className="df-perm-modal-head">
          <div>
            <span className="df-perm-kicker">功能权限</span>
            <h2>设置用户可用功能</h2>
            <p>勾选该用户可使用的功能模块。未勾选时，用户点击数字员工或智能中心功能会收到无权限提示。</p>
          </div>
          <div className="df-perm-count">6项</div>
        </div>
        <div className="df-perm-grid">
          {featurePermissions.map(f=>(
            <label key={f.key} className={`df-perm-card ${permFeatures.includes(f.key) ? 'is-enabled' : ''}`}>
              <Checkbox checked={permFeatures.includes(f.key)} onChange={e=>{
                setPermFeatures(prev=>e.target.checked?[...prev,f.key]:prev.filter(k=>k!==f.key));
              }} />
              <div>
                <div className="df-perm-card-title">{f.label}</div>
                <div className="df-perm-card-desc">{f.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </Modal>

      <Modal title="可配置功能范围" open={featureInfoOpen} onCancel={()=>setFeatureInfoOpen(false)} footer={<Button type="primary" onClick={()=>setFeatureInfoOpen(false)}>知道了</Button>} width={620}>
        <div style={{marginBottom:12,color:'#64748b',fontSize:13}}>当前工作台可配置数字员工和智能中心内共 6 个功能。管理员在用户列表中点击“权限”即可逐个用户开关。</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          {featurePermissions.map(f=>(
            <div key={f.key} style={{padding:'10px 12px',background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:10}}>
              <div style={{fontSize:13,fontWeight:700,color:'#0f172a'}}>{f.label}</div>
              <div style={{fontSize:11,color:'#94a3b8'}}>{f.desc}</div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
